import React, { useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { motion, useAnimation } from "framer-motion";
import styles from "./StockList.module.css";
import { useHistory } from "react-router-dom";

import StockItem from "../StockItem";
import ColumnStockItem from "components/ColumnStockItem";

export function StockList({
  stockInfo,
  pageFrom,
  listId,
  isList,
  isLoading,
  onDelete,
  showNote,
  forceListLayout,
  removeFromListOnAddToCart,
}) {
  let history = useHistory();

  // Switching between list and column changes margins, padding and item
  // positions. The list is remounted so the switch reads as an instant mode
  // change rather than items animating between two unrelated layouts.
  const prevIsList = useRef(isList);
  const layoutModeChanged = prevIsList.current !== isList;
  useEffect(() => {
    prevIsList.current = isList;
  }, [isList]);

  // The list animates as a single unit. Animating each row instead would mean
  // one measured height animation per item, which scales with catalogue size
  // and degrades badly once a filter swaps out hundreds of rows at once.
  // Previously a new stockInfo identity bumped a version number baked into
  // the container's `key`, forcing React to unmount/remount every StockItem
  // (each of which carries its own state/effects). That full remount is what
  // made rapid filter clicks feel laggy or get dropped. Replaying the fade
  // via imperative animation controls gets the same visual effect without
  // tearing down and recreating the item components.
  const controls = useAnimation();

  // The downward slide is a page-entry flourish. Replaying it every time a
  // filter changes would be noise, so later passes just cross-fade.
  const hasMounted = useRef(false);
  useEffect(() => {
    hasMounted.current = true;
  }, []);

  const prevStockInfo = useRef(stockInfo);
  useEffect(() => {
    if (prevStockInfo.current === stockInfo) return;
    prevStockInfo.current = stockInfo;

    // Layout mode changes remount the container via `key` below (margins/
    // padding genuinely differ between list and column), so the mount
    // animation already handles that transition; only replay the fade here
    // for same-layout filter/sort changes.
    if (layoutModeChanged) return;

    controls.set({ opacity: 0 });
    controls.start({
      opacity: 1,
      transition: { duration: 0.28, ease: "easeOut" },
    });
  }, [stockInfo, layoutModeChanged, controls]);

  // `animate` is driven by controls rather than a static target, so the
  // mount entrance (fade, or fade+slide on first page load) has to be
  // kicked off explicitly here once per mount/remount.
  useEffect(() => {
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.28, ease: "easeOut" },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initial = layoutModeChanged
    ? false
    : hasMounted.current
    ? { opacity: 0 }
    : { opacity: 0, y: -40 };

  // Inline arrow props would give every item a new onClick on each parent
  // render, defeating React.memo on StockItem/ColumnStockItem. Building the
  // handlers once per list keeps their identity stable.
  const clickHandlers = useMemo(() => {
    const handlers = {};
    stockInfo.forEach((item) => {
      handlers[item.stockId] = () =>
        history.push({
          pathname: `/stock-details/${item.stockId}`,
          state: { pageFrom, listId },
        });
    });
    return handlers;
  }, [stockInfo, history, pageFrom, listId]);

  return (
    <motion.div
      key={`${isLoading ? "loading" : "loaded"}-${isList ? "list" : "column"}`}
      className={`${styles.container} ${isList ? "" : styles.column}`}
      initial={initial}
      animate={controls}
      transition={{ duration: 0.28, ease: "easeOut" }}
    >
      {stockInfo.map((item, index) => (
        <div
          className={`${styles.stockItem} ${isList ? "" : styles.column}`}
          key={item.listProductId ?? `${item.stockId}-${item.variationId ?? item.variantId ?? ""}-${index}`}
        >
          {isList ? (
            <StockItem
              stockInfo={item}
              onDelete={onDelete}
              showNote={showNote}
              onClick={clickHandlers[item.stockId]}
              forceListLayout={forceListLayout}
              removeFromListOnAddToCart={removeFromListOnAddToCart}
            />
          ) : (
            <>
              <ColumnStockItem
                stockInfo={item}
                onClick={clickHandlers[item.stockId]}
              />
              <div className={styles.borderLine}></div>
            </>
          )}
        </div>
      ))}
    </motion.div>
  );
}

StockList.propTypes = {
  stockInfo: PropTypes.arrayOf(PropTypes.shape),

  pageFrom: PropTypes.string,
  listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  onDelete: PropTypes.func,
  showNote: PropTypes.bool,
  forceListLayout: PropTypes.bool,
  removeFromListOnAddToCart: PropTypes.bool,
};
